// frontend/src/pages/Profile.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import VideoPlayer from '../components/VideoPlayer.js';
import { toast } from 'sonner';
import { onMessage } from '../firebase';
import Navbar from '../components/Navbar';
import { Meteors } from '../components/ui/meteors';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, User, MessageCircle, Image, Video, Search, BarChart3, Star, Trophy, Pencil, Plus, Save, X } from 'lucide-react';
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import {
  Empty,
  EmptyTitle,
  EmptyDescription,
  EmptyMedia,
} from '../components/ui/empty';
import {
  Item,
  ItemContent,
} from "../components/ui/item";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card"
import { useTheme } from '../contexts/ThemeContext';

const isReactSnap = navigator.userAgent === 'ReactSnap';

const API_URL = process.env.REACT_APP_API_URL;

// Utility to debounce a function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const fetchUser = async () => {
  const response = await axios.get(`${API_URL}/api/profile/`, { withCredentials: true });
  return response.data;
};

const fetchChatMessages = async (partnerId) => {
  if (!partnerId) return [];
  const response = await axios.get(`${API_URL}/api/chat-messages/?partner=${partnerId}`, { withCredentials: true });
  return response.data.messages || [];
};

const fetchChatPartners = async () => {
  const response = await axios.get(`${API_URL}/api/chat-messages/`, { withCredentials: true });
  const messages = response.data.messages || [];
  const user = (await axios.get(`${API_URL}/api/profile/`, { withCredentials: true })).data;
  const partners = new Set();
  messages.forEach((msg) => {
    if (msg.sender !== user.id && msg.sender_username) {
      partners.add(JSON.stringify({ id: msg.sender, username: msg.sender_username }));
    }
    if (msg.receiver !== user.id && msg.receiver_username) {
      partners.add(JSON.stringify({ id: msg.receiver, username: msg.receiver_username }));
    }
  });
  return Array.from(partners).map((p) => JSON.parse(p));
};

const fetchCanChat = async (partnerId) => {
  if (!partnerId) return { can_chat: false, error: 'No partner selected' };
  const response = await axios.get(`${API_URL}/api/can_chat/${partnerId}/`, { withCredentials: true });
  return response.data;
};

const connectDebtWebSocket = (userId, setDebtPending, setDebtError, queryClient, debtWsRef) => {
  if (!userId) {
    console.warn('No userId provided for Debt WebSocket');
    return;
  }
  const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  const wsUrl = `${wsProtocol}${process.env.REACT_APP_API_URL.replace(/^https?:\/\//, '')}/ws/debt/`;
  const ws = new WebSocket(wsUrl, [], {
    headers: {
      Cookie: document.cookie, // Send sessionid for authentication
    },
  });

  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 5000; // 5 seconds
  const maxReconnectDelay = 60000; // 60 seconds

  ws.onopen = () => {
    console.log(`Debt WebSocket connected for user ${userId}`);
    reconnectAttempts = 0;
  };

  ws.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      console.log('Debt WebSocket message received:', JSON.stringify(data, null, 2));
      if (data.total_debt !== undefined && data.updated_at) {
        const newDebt = parseFloat(data.total_debt);
        let shouldUpdate = true;
        // Update user debt
        queryClient.setQueryData(['user'], (old) => {
          console.log('Old user debt:', old?.total_debt, 'New debt:', newDebt, 'Is service provider:', old?.is_service_provider);
          if (old?.is_service_provider && newDebt === 0) {
            console.warn(`Skipping user debt update to 0 for service provider ${userId}`);
            shouldUpdate = false;
            return old;
          }
          return {
            ...old,
            total_debt: newDebt,
          };
        });
        // Update providerOrders debt
        queryClient.setQueryData(['providerOrders'], (old) => {
          console.log('Old providerOrders debt:', old?.total_debt, 'New debt:', newDebt);
          if (old && old.total_debt != null && newDebt === 0) {
            console.warn(`Skipping providerOrders debt update to 0 for user ${userId}`);
            shouldUpdate = false;
            return old;
          }
          return {
            ...old,
            total_debt: newDebt,
            can_accept_orders: newDebt <= 500,
            debt_message: newDebt > 500
              ? `Cannot accept new orders: debt (${newDebt} KES) exceeds threshold (500 KES)`
              : null,
          };
        });
        if (shouldUpdate) {
          queryClient.invalidateQueries(['providerOrders']);
          queryClient.invalidateQueries(['user']); // Ensure UI updates with new debt
          setDebtPending(false);
          toast.success(`Debt updated to ${newDebt} KES`);
        }
      } else if (data.error) {
        console.error('Debt WebSocket error:', data.error);
        setDebtError(data.error);
        toast.error(data.error);
      } else {
        console.warn('Invalid WebSocket message:', data);
      }
    } catch (err) {
      console.error('Debt WebSocket message parse error:', err);
    }
  };
  ws.onclose = (event) => {
    console.log(`Debt WebSocket closed for user ${userId}. Code: ${event.code}, Reason: ${event.reason}`);
    if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
      const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts), maxReconnectDelay);
      reconnectAttempts++;
      console.log(`Debt WebSocket reconnect attempt ${reconnectAttempts} after ${delay}ms`);
      setTimeout(() => {
        debtWsRef.current = connectDebtWebSocket(userId, setDebtPending, setDebtError, queryClient, debtWsRef);
      }, delay);
    } else if (event.code !== 1000) {
      console.error(`Debt WebSocket max reconnect attempts (${maxReconnectAttempts}) reached`);
      toast.error('Failed to reconnect Debt WebSocket. Please refresh the page.');
    }
  };

  ws.onerror = (error) => {
    console.error('Debt WebSocket error:', error);
  };

  debtWsRef.current = ws;
};

const connectStatusWebSocket = (userId, queryClient, statusWsRef) => {
  if (!userId) {
    console.warn('No userId provided for Status WebSocket');
    return;
  }
  const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  const wsUrl = `${wsProtocol}${process.env.REACT_APP_API_URL.replace(/^https?:\/\//, '')}/ws/status/`;
  const ws = new WebSocket(wsUrl, [], {
    headers: {
      Cookie: document.cookie, // Send sessionid for authentication
    },
  });

  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 5000; // 5 seconds
  const maxReconnectDelay = 60000; // 60 seconds

  ws.onopen = () => {
    console.log(`Status WebSocket connected for user ${userId}`);
    reconnectAttempts = 0;
  };

  ws.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      console.log('Status WebSocket message received:', JSON.stringify(data, null, 2));
      if (data.type === 'rating_update') {
        queryClient.invalidateQueries(['providerOrders']);
        queryClient.invalidateQueries(['clientOrders']);
        queryClient.invalidateQueries(['user']);
        toast.success('Rating updated');
      } else if (data.error) {
        console.error('Status WebSocket error:', data.error);
        toast.error(data.error);
      } else {
        console.warn('Invalid Status WebSocket message:', data);
      }
    } catch (err) {
      console.error('Status WebSocket message parse error:', err);
    }
  };

  ws.onclose = (event) => {
    console.log(`Status WebSocket closed for user ${userId}. Code: ${event.code}, Reason: ${event.reason}`);
    if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
      const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts), maxReconnectDelay);
      reconnectAttempts++;
      console.log(`Status WebSocket reconnect attempt ${reconnectAttempts} after ${delay}ms`);
      setTimeout(() => {
        statusWsRef.current = connectStatusWebSocket(userId, queryClient, statusWsRef);
      }, delay);
    } else if (event.code !== 1000) {
      console.error(`Status WebSocket max reconnect attempts (${maxReconnectAttempts}) reached`);
      toast.error('Failed to reconnect Status WebSocket. Please refresh the page.');
    }
  };

  ws.onerror = (error) => {
    console.error('Status WebSocket error:', error);
  };

  statusWsRef.current = ws;
};

const fetchCategories = async () => {
  const response = await axios.get(`${API_URL}/api/categories/`, { withCredentials: true });
  return response.data;
};

const fetchServices = async () => {
  const response = await axios.get(`${API_URL}/api/services/`, { withCredentials: true });
  return response.data;
};

const fetchProviders = async () => {
  const response = await axios.get(`${API_URL}/api/providers/`, { withCredentials: true });
  return response.data.providers || [];
};

const fetchProviderImages = async (providerId) => {
  const response = await axios.get(`${API_URL}/api/provider/${providerId}/images/`, { withCredentials: true });
  // Ensure image URLs use HTTPS
  const images = response.data.map(img => ({
    ...img,
    image: img.image.replace(/^http:\/\//, 'https://'),
  }));
  return images;
};

function Profile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [chatSocket, setChatSocket] = useState(null);
  const location = useLocation();
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [debtAmount, setDebtAmount] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [debtError, setDebtError] = useState('');
  const [showScoringPanel, setShowScoringPanel] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    location: '',
    business_name: '',
    service_provided: '',
    description: '',
    business_hours: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
  const [serviceForm, setServiceForm] = useState({ name: '', category: '', description: '', custom_minimum_charge: '' });
  const [serviceFormErrors, setServiceFormErrors] = useState({});
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [assignServiceForm, setAssignServiceForm] = useState({ provider_id: '', service_name: '', category: '' });
  const [assignServiceErrors, setAssignServiceErrors] = useState({});
  const [isDebtPending, setIsDebtPending] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoError, setVideoError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [imageProgress, setImageProgress] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const messagesEndRef = useRef(null);
  const prevPartnerRef = useRef(null);
  const debtWsRef = useRef(null);
  const statusWsRef = useRef(null);
  const { darkMode, toggleDarkMode } = useTheme();

  const { user, userLoading, userError } = useAuth();
  const connectChatWebSocket = useCallback(
    (partnerId, setMessages, queryClient) => {
      if (!partnerId) {
        console.warn('No partnerId provided for chat WebSocket');
        return null;
      }
      const ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/ws/chat/${partnerId}/`);
      console.log(`Connecting to Chat WebSocket for partner ${partnerId}: ${ws.url}`);

      ws.onopen = () => {
        console.log(`Chat WebSocket connected for partner ${partnerId}`);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`Chat WebSocket message received for partner ${partnerId}:`, JSON.stringify(data, null, 2));
          if (data.message && data.id) {
            setMessages((prev) => {
              const updatedMessages = prev.filter((msg) => !String(msg.id).startsWith('temp-'));
              if (!updatedMessages.some((msg) => msg.id === data.id)) {
                updatedMessages.push({
                  id: data.id,
                  message: data.message,
                  sender: data.sender,
                  sender_username: data.sender_username,
                  timestamp: data.timestamp,
                });
                console.log(`Added new message for partner ${partnerId}: ${data.message} (ID: ${data.id})`);
              } else {
                console.log(`Message ID ${data.id} already exists, skipping`);
              }
              return updatedMessages;
            });
            toast.success('New chat message received');
          } else if (data.messages) {
            setMessages(data.messages);
            setIsChatLoading(false); // Clear loading state when WebSocket provides initial messages
            console.log(`Received initial messages for partner ${partnerId}: ${data.messages.length} messages`);
          } else if (data.error) {
            console.error(`Chat WebSocket error for partner ${partnerId}: ${data.error}`);
            if (canChatData?.can_chat) {
              toast.error(data.error);
            }
          } else {
            console.warn(`Invalid WebSocket message for partner ${partnerId}:`, data);
          }
        } catch (err) {
          console.error(`Chat WebSocket message parse error for partner ${partnerId}:`, err);
        }
      };

      ws.onclose = (event) => {
        console.log(`Chat WebSocket closed for partner ${partnerId}. Code: ${event.code}, Reason: ${event.reason}`);
      };

      ws.onerror = (error) => {
        console.error(`Chat WebSocket error for partner ${partnerId}:`, error);
        // Avoid showing toast for expected errors (e.g., no active order)
        if (canChatData?.can_chat) {
          toast.error('Chat connection error');
        }
      };

      return ws;
    },
    []
  );

  const { data: chatPartners, refetch: refetchPartners } = useQuery({
    queryKey: ['chatPartners'],
    queryFn: fetchChatPartners,
    enabled: !!user && !isReactSnap,
  });

  const { data: chatMessagesData, refetch: refetchMessages, isFetching: isMessagesFetching } = useQuery({
    queryKey: ['chatMessages', selectedPartner],
    queryFn: () => fetchChatMessages(selectedPartner),
    enabled: !!selectedPartner && !!user && !isReactSnap,
    refetchOnWindowFocus: false,
  });

  const { data: canChatData, error: canChatError } = useQuery({
    queryKey: ['canChat', selectedPartner],
    queryFn: () => fetchCanChat(selectedPartner),
    enabled: !!selectedPartner && !!user && !isReactSnap,
    refetchOnWindowFocus: false,
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    enabled: !!user?.is_superuser && !isReactSnap,
  });

  const { data: services, isLoading: servicesLoading } = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices,
    enabled: !!user && !isReactSnap,
  });

  const { data: providers, isLoading: providersLoading } = useQuery({
    queryKey: ['providers'],
    queryFn: fetchProviders,
    enabled: !!user?.is_superuser && !isReactSnap,
  });

  const { data: providerImages, isLoading: imagesLoading } = useQuery({
    queryKey: ['providerImages', user?.provider?.id],
    queryFn: () => fetchProviderImages(user.provider.id),
    enabled: !!user?.is_service_provider && !!user?.provider?.id && !isReactSnap,
  });

  const { data: providerVideos = [], isLoading: videosLoading } = useQuery({
    queryKey: ['providerVideos', user?.provider?.id],
    queryFn: async () => {
      if (!user?.provider?.id) return [];
      const res = await axios.get(`${API_URL}/api/provider/${user.provider.id}/videos/`, { withCredentials: true });
      return res.data;
    },
    enabled: !!user?.is_service_provider && !!user?.provider?.id,
  });

  const role = useMemo(() => (
    user ? (user.is_superuser ? 'Administrator' : user.is_service_provider ? 'Service Provider' : 'Client') : 'Client'
  ), [user]);

  const reputationColor = useMemo(() => (
    user && user.reputation_score >= 75 ? 'text-green-400' : user && user.reputation_score <= 25 ? 'text-red-400' : 'text-white'
  ), [user]);

  const trustColor = useMemo(() => (
    user && user.provider && ['A', 'B'].includes(user.provider.trust_tier)
      ? 'text-green-400'
      : user && user.provider && user.provider.trust_tier === 'E'
      ? 'text-red-400'
      : 'text-white'
  ), [user]);

  const { data: providerOrders } = useQuery({
    queryKey: ['providerOrders'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/provider_orders/`, { withCredentials: true });
      return response.data;
    },
    enabled: !!user?.is_service_provider && !isReactSnap,
    initialData: isReactSnap ? null : undefined,
  });

  const totalDebt = useMemo(() => {
    if (user?.is_service_provider && providerOrders?.total_debt !== undefined) {
      console.log('Using providerOrders.total_debt:', providerOrders.total_debt);
      return providerOrders.total_debt;
    }
    console.log('Using user.total_debt:', user?.total_debt || 0);
    return user ? user.total_debt || 0 : 0;
  }, [user, providerOrders]);

  const debtThreshold = 500;
  const isDebtHigh = totalDebt > debtThreshold;

  const uploadImageMutation = useMutation({
    mutationFn: async () => {
      if (!imageFile) throw new Error("No image selected");
      const formData = new FormData();
      formData.append('image', imageFile);

      return axios.post(`${API_URL}/api/provider/upload-image/`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setImageProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
    },
    onMutate: () => {
      setImageProgress(10);
      const interval = setInterval(() => {
        setImageProgress(p => p >= 90 ? 90 : p + 15);
      }, 400);
      return { interval };
    },
    onSuccess: () => {
      setImageProgress(100);
      setTimeout(() => {
        toast.success('Image uploaded!');
        queryClient.invalidateQueries(['providerImages']);
        setImageFile(null);
        setImageProgress(0);
      }, 500);
    },
    onError: () => {
      setImageError('Upload failed');
      toast.error('Failed');
      setImageProgress(0);
    },
    onSettled: (_, __, ___, context) => {
      clearInterval(context?.interval);
    }
  });

  const deleteImageMutation = useMutation({
    mutationFn: async (imageId) => {
      await axios.delete(`${API_URL}/api/image/${imageId}/delete/`, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Image deleted successfully!');
      queryClient.invalidateQueries(['providerImages']);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.error || 'Failed to delete image.';
      toast.error(errorMsg);
    },
  });

  const uploadVideoMutation = useMutation({
    mutationFn: async () => {
      if (!videoFile) throw new Error("No video selected");
      const formData = new FormData();
      formData.append('video', videoFile);

      return axios.post(`${API_URL}/api/provider/upload-video/`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
    },
    onMutate: () => {
      setUploadProgress(5); // Start progress immediately
      // Fake progress animation
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + Math.floor(Math.random() * 12);
        });
      }, 800);
      return { interval };
    },
    onSuccess: () => {
      setUploadProgress(100);
      setTimeout(() => {
        toast.success('Video ready!');
        queryClient.invalidateQueries(['providerVideos']);
        setVideoFile(null);
        setUploadProgress(0);
      }, 600);
    },
    onError: (err, _, context) => {
      clearInterval(context?.interval);
      setVideoError(err.response?.data?.error || 'Upload failed');
      toast.error('Failed');
      setUploadProgress(0);
    },
    onSettled: (_, __, ___, context) => {
      clearInterval(context?.interval);
    }
  });

  const deleteVideoMutation = useMutation({
    mutationFn: async (videoId) => {
      await axios.post(`${API_URL}/api/video/${videoId}/delete/`, {}, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Video deleted successfully!');
      queryClient.invalidateQueries(['providerVideos']);
    },
    onError: () => {
      toast.error('Failed to delete video.');
    },
  });
  useEffect(() => {
    if (!isChatLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isChatLoading]);

  useEffect(() => {
    if (user) {
      setEditFormData({
        username: user.username || '',
        email: user.email || '',
        location: user.location || '',
        business_name: user.provider?.business_name || '',
        service_provided: user.provider?.service_provided || '',
        description: user.provider?.description || '',
        business_hours: user.provider?.business_hours || '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (canChatError) {
      toast.error(canChatError.response?.data?.error || 'Failed to check chat eligibility.');
    }
  }, [canChatError]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  useEffect(() => {
    console.log('useEffect triggered - selectedPartner:', selectedPartner, 'chatMessagesData:', chatMessagesData);
    if (!selectedPartner || !chatMessagesData) return;

    setChatMessages(chatMessagesData);
    setIsChatLoading(false);
    prevPartnerRef.current = selectedPartner;
    console.log(`Initialized chatMessages for partner ${selectedPartner} with ${chatMessagesData.length} messages`);
  }, [chatMessagesData, selectedPartner]);

  useEffect(() => {
    if (!user || !selectedPartner || !canChatData?.can_chat) {
      console.log(`Skipping WebSocket connection for partner ${selectedPartner}: user=${!!user}, canChat=${canChatData?.can_chat}`);
      return;
    }

    let ws = null;
    let reconnectTimeout = null;
    let isClosing = false;

    const connect = () => {
      if (isClosing) return;
      ws = connectChatWebSocket(selectedPartner, setChatMessages, queryClient);
      if (!ws) return;

      ws.onclose = (event) => {
        if (isClosing) return;
        console.log(`Chat WebSocket closed for partner ${selectedPartner}. Code: ${event.code}, Reason: ${event.reason}`);
        if (event.code !== 1000) {
          reconnectTimeout = setTimeout(() => {
            console.log(`Reconnecting WebSocket for partner ${selectedPartner}...`);
            connect();
          }, 5000);
        }
      };

      setChatSocket(ws);
    };

    connect();

    return () => {
      isClosing = true;
      if (ws) {
        console.log(`Closing WebSocket for partner ${selectedPartner}`);
        ws.close(1000, 'Component unmounted');
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [user, selectedPartner, canChatData, connectChatWebSocket, queryClient]);

  useEffect(() => {
    const debtWsRef = { current: null };
    let isClosing = false;

    if (user?.id) {
      connectDebtWebSocket(user.id, setIsDebtPending, setDebtError, queryClient, debtWsRef);
    }

    return () => {
      isClosing = true;
      if (debtWsRef.current) {
        console.log(`Closing Debt WebSocket for user ${user?.id}`);
        debtWsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [user?.id, queryClient, setIsDebtPending, setDebtError]);

  useEffect(() => {
    const statusWsRef = { current: null };
    let isClosing = false;

    if (user?.id) {
      connectStatusWebSocket(user.id, queryClient, statusWsRef);
    }

    return () => {
      isClosing = true;
      if (statusWsRef.current) {
        console.log(`Closing Status WebSocket for user ${user?.id}`);
        statusWsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [user?.id, queryClient]);
  
  // FCM Notification Listener for Chat
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onMessage((payload) => {
      console.log('FCM message received:', payload);
      const data = payload.data || {};

      if (data.type === 'chat_message' && data.partnerId !== selectedPartner) {
        // Notify only if the message is from a different chat
        toast.success(`New message from ${data.sender_username}`);
        refetchPartners(); // Update chat partners list
        refetchMessages(); // Refresh messages if the user switches to this chat
      }
    });

    return () => unsubscribe();
  }, [user, selectedPartner, refetchPartners, refetchMessages]);

  const debouncedSetPartner = useCallback(
    debounce((partnerId) => {
      setIsChatLoading(true);
      setSelectedPartner(partnerId);
      prevPartnerRef.current = partnerId;
      refetchMessages();
    }, 300),
    [refetchMessages]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chatWith = params.get('chat_with');
    if (chatWith && chatWith !== selectedPartner) {
      debouncedSetPartner(chatWith);
      refetchPartners();
    }
  }, [location, refetchPartners, debouncedSetPartner, selectedPartner]);

  const offsetDebtMutation = useMutation({
    mutationFn: async ({ amount, phone_number }) => {
      const response = await axios.post(`${API_URL}/api/debt/offset/`, { amount, phone_number }, { withCredentials: true });
      return response.data;
    },
    onSuccess: async (data) => {
      toast.success(data.message || 'Payment submitted for validation.');
      setIsDebtPending(true);
      await queryClient.invalidateQueries(['user']);
      await queryClient.refetchQueries(['user']);
      queryClient.invalidateQueries(['providerOrders']);
      setDebtAmount('');
      setPhoneNumber('');
      setDebtError('');
    },
    onError: (err) => {
      setIsDebtPending(false);
      const errorMsg = err.response?.data?.error || err.response?.data?.phone_number?.[0] || 'Failed to submit payment.';
      setDebtError(errorMsg);
      toast.error(errorMsg);
    },
  });

  const editProfileMutation = useMutation({
    mutationFn: async (formData) => {
      console.log('Sending edit profile request:', formData);
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });
      const response = await axios.post(`${API_URL}/api/edit-profile/`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('Edit profile response:', response.data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['user']);
      queryClient.invalidateQueries(['searchProviders']);
      setShowEditForm(false);
      setEditFormErrors({});
    },
    onError: (err) => {
      console.error('Edit profile error:', err.response?.data);
      let errorMsg = { all: ['Failed to update profile.'] };
      if (err.response?.status === 404) {
        errorMsg = { all: ['Profile update endpoint not found. Please contact support.'] };
      } else if (err.response?.data?.errors) {
        errorMsg = err.response.data.errors;
      }
      setEditFormErrors(errorMsg);
      toast.error(errorMsg.all?.join(' ') || 'Failed to update profile.');
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({ partnerId, message }) => {
      const response = await axios.post(
        `${API_URL}/api/chat/${partnerId}/`,
        { message },
        { withCredentials: true }
      );
      return response.data;
    },
    onMutate: async ({ partnerId, message }) => {
      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        message,
        sender: user.id,
        sender_username: user.username,
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, optimisticMessage]);
      setMessage('');
      console.log(`Optimistic message added: ${message} to partner ${partnerId}`);
      return { tempId };
    },
    onSuccess: (data, variables, context) => {
      // Update the message with server data
      setChatMessages((prev) => {
        const updatedMessages = prev.filter((msg) => msg.id !== context.tempId);
        if (data.id && !updatedMessages.some((msg) => msg.id === data.id)) {
          updatedMessages.push({
            id: data.id,
            message: data.message,
            sender: data.sender,
            sender_username: data.sender_username,
            timestamp: data.timestamp,
          });
        }
        return updatedMessages;
      });
      toast.success('Message sent!');
      console.log(`Message sent successfully to partner ${variables.partnerId}: ${variables.message}`);
    },
    onError: (err, variables, context) => {
      setChatMessages((prev) => prev.filter((msg) => msg.id !== context.tempId));
      const errorMsg = err.response?.data?.error || 'Failed to send message. Ensure you have a scheduled or active order.';
      toast.error(errorMsg);
      console.error(`Failed to send message to partner ${variables.partnerId}: ${errorMsg}`);
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (categoryData) => {
      const response = editingCategoryId
        ? await axios.put(`${API_URL}/api/categories/${editingCategoryId}/`, categoryData, { withCredentials: true })
        : await axios.post(`${API_URL}/api/categories/`, categoryData, { withCredentials: true });
      return response.data;
    },
    onSuccess: () => {
      toast.success(editingCategoryId ? 'Category updated!' : 'Category created!');
      queryClient.invalidateQueries(['categories']);
      setIsCategoryModalOpen(false);
      setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
      setEditingCategoryId(null);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to save category.'] };
      setEditFormErrors(errorMsg);
      toast.error(errorMsg.all?.join(' ') || 'Failed to save category.');
    },
  });

  const createServiceMutation = useMutation({
    mutationFn: async (serviceData) => {
      const payload = { ...serviceData };
      if (payload.custom_minimum_charge === '' || isNaN(payload.custom_minimum_charge)) {
        delete payload.custom_minimum_charge;
      } else {
        payload.custom_minimum_charge = parseFloat(payload.custom_minimum_charge);
      }
      const response = editingServiceId
        ? await axios.put(`${API_URL}/api/services/${editingServiceId}/`, payload, { withCredentials: true })
        : await axios.post(`${API_URL}/api/services/`, payload, { withCredentials: true });
      return response.data;
    },
    onSuccess: () => {
      toast.success(editingServiceId ? 'Service updated!' : 'Service created!');
      queryClient.invalidateQueries(['services']);
      setIsServiceModalOpen(false);
      setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
      setServiceFormErrors({});
      setEditingServiceId(null);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to save service.'] };
      setServiceFormErrors(errorMsg);
      toast.error(errorMsg.all?.join(' ') || Object.values(errorMsg).flat().join(' ') || 'Failed to save service.');
    },
  });

  const assignServiceMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post(`${API_URL}/api/assign-service/`, formData, { withCredentials: true });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['providers']);
      queryClient.invalidateQueries(['services']);
      queryClient.invalidateQueries(['user']);
      setAssignServiceForm({ provider_id: '', service_name: '', category: '' });
      setAssignServiceErrors({});
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.error || 'Failed to assign service.';
      setAssignServiceErrors({ all: [errorMsg] });
      toast.error(errorMsg);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId) => {
      await axios.delete(`${API_URL}/api/categories/${categoryId}/`, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Category deleted!');
      queryClient.invalidateQueries(['categories']);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to delete category.'] };
      toast.error(errorMsg.all?.join(' ') || 'Failed to delete category.');
    },
  });

  const deleteServiceMutation = useMutation({
    mutationFn: async (serviceId) => {
      await axios.delete(`${API_URL}/api/services/${serviceId}/`, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Service deleted!');
      queryClient.invalidateQueries(['services']);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to delete service.'] };
      toast.error(errorMsg.all?.join(' ') || 'Failed to delete service.');
    },
  });

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      setIsMenuOpen(false);
      toast.success('Logged out successfully.');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleSendMessage = useCallback(() => {
    if (!selectedPartner || !message.trim()) {
      toast.error('Please select a partner and enter a message.');
      return;
    }
    if (!canChatData?.can_chat) {
      toast.error(canChatData?.error || 'You cannot send messages to this user at this time.');
      return;
    }
    sendMessageMutation.mutate({ partnerId: selectedPartner, message });
  }, [selectedPartner, message, sendMessageMutation, canChatData]);

  const handlePartnerClick = useCallback((partnerId) => {
    setSelectedPartner(partnerId.toString());
  }, []);

  const handleOffsetDebt = useCallback(() => {
    if (!debtAmount || isNaN(debtAmount) || parseFloat(debtAmount) <= 0) {
      setDebtError('Please enter a valid amount (greater than 0).');
      toast.error('Invalid amount.');
      return;
    }
    if (parseFloat(debtAmount) > (user.total_debt || 0)) {
      setDebtError('Amount cannot exceed total debt.');
      toast.error('Amount exceeds total debt.');
      return;
    }
    if (!phoneNumber || !/2547[0-1][0-9]{7}/.test(phoneNumber)) {
      setDebtError('Please enter a valid M-Pesa number (2547XXXXXXXX).');
      toast.error('Invalid M-Pesa number.');
      return;
    }
    offsetDebtMutation.mutate({ amount: parseFloat(debtAmount), phone_number: phoneNumber });
  }, [debtAmount, phoneNumber, user, offsetDebtMutation]);

  const handleEditProfile = () => {
    console.log('handleEditProfile called with formData:', editFormData);
    const errors = {};
    if (!editFormData.username.trim()) errors.username = 'Username is required.';
    if (!editFormData.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(editFormData.email)) {
      errors.email = 'Invalid email format.';
    }
    if (!editFormData.location.trim()) errors.location = 'Location is required.';
    if (user?.is_service_provider) {
      if (!editFormData.business_name.trim()) errors.business_name = 'Business name is required.';
      if (!editFormData.service_provided.trim()) errors.service_provided = 'Service provided is required.';
    }

    if (Object.keys(errors).length > 0) {
      console.log('Validation errors:', errors);
      setEditFormErrors(errors);
      toast.error('Please fix the errors in the form.');
      return;
    }

    console.log('Calling editProfileMutation.mutate');
    editProfileMutation.mutate(editFormData);
  };

  const handleCategoryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryForm({ ...categoryForm, [name]: type === 'checkbox' ? checked : value });
    setEditFormErrors({});
  };

  const handleServiceInputChange = (e) => {
    const { name, value } = e.target;
    setServiceForm({ ...serviceForm, [name]: value });
    setServiceFormErrors({});
  };

  const handleAssignServiceInputChange = (e) => {
    const { name, value } = e.target;
    setAssignServiceForm({ ...assignServiceForm, [name]: value });
    setAssignServiceErrors({});
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      default_minimum_charge: category.default_minimum_charge || '',
      is_general: category.is_general || false,
    });
    setEditingCategoryId(category.id);
    setIsCategoryModalOpen(true);
  };

  const handleEditService = (service) => {
    const isGeneral = categories?.find(cat => cat.slug === service.category)?.is_general || false;
    setServiceForm({
      name: service.name,
      category: service.category,
      description: service.description || '',
      custom_minimum_charge: isGeneral ? (service.custom_minimum_charge || '300.00') : '',
    });
    setEditingServiceId(service.id);
    setIsServiceModalOpen(true);
  };

  const validateServiceForm = () => {
    const errors = {};
    if (!serviceForm.name.trim()) {
      errors.name = 'Service name is required.';
    }
    if (!serviceForm.category) {
      errors.category = 'Category is required.';
    }
    const isGeneral = categories?.find(cat => cat.slug === serviceForm.category)?.is_general || false;
    if (isGeneral) {
      if (!serviceForm.custom_minimum_charge || isNaN(serviceForm.custom_minimum_charge) || parseFloat(serviceForm.custom_minimum_charge) <= 0) {
        errors.custom_minimum_charge = 'A positive custom minimum charge is required for General Services.';
      }
    } else if (serviceForm.custom_minimum_charge) {
      errors.custom_minimum_charge = 'Custom minimum charge is only allowed for General Services.';
    }
    return errors;
  };

  const handleCreateCategory = (e) => {
    e.preventDefault();
    createCategoryMutation.mutate(categoryForm);
  };

  const handleCreateService = (e) => {
    e.preventDefault();
    const errors = validateServiceForm();
    if (Object.keys(errors).length > 0) {
      setServiceFormErrors(errors);
      toast.error('Please fix the errors in the form.');
      return;
    }
    createServiceMutation.mutate(serviceForm);
  };

  const handleAssignService = (e) => {
    e.preventDefault();
    const errors = {};
    if (!assignServiceForm.provider_id) errors.provider_id = 'Please select a provider.';
    if (!assignServiceForm.service_name.trim()) errors.service_name = 'Service name is required.';
    if (!assignServiceForm.category) errors.category = 'Please select a category.';

    if (Object.keys(errors).length > 0) {
      setAssignServiceErrors(errors);
      toast.error('Please fix the errors in the form.');
      return;
    }

    assignServiceMutation.mutate(assignServiceForm);
  };

  const handleDeleteCategory = (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      deleteCategoryMutation.mutate(categoryId);
    }
  };

  const handleDeleteService = (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      deleteServiceMutation.mutate(serviceId);
    }
  };

  const handleImageUpload = (e) => {
    e.preventDefault();
    if (!imageFile) {
      setImageError('Please select an image.');
      toast.error('Please select an image.');
      return;
    }
    uploadImageMutation.mutate();
  };

  const handleDeleteImage = (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteImageMutation.mutate(imageId);
    }
  };

  const handleVideoUpload = (e) => {
    e.preventDefault();
    if (!videoFile) {
      setVideoError('Please select a video.');
      toast.error('Please select a video.');
      return;
    }
    uploadVideoMutation.mutate();
  };

  const handleDeleteVideo = (videoId) => {
    if (window.confirm('Delete this video?')) {
      deleteVideoMutation.mutate(videoId);
    }
  };

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-lg">Loading your profile...</p>
        </div>
      </div>
    );
  }
  if (userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-lg">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user || userError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        </div>

        <div className="relative z-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 border border-gray-700 backdrop-blur-lg">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Access Required 🔐</h2>
            <p className="text-gray-300 mb-8 leading-relaxed">This page is only available to users with accounts. Join the SErraND community today!</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                onClick={() => navigate('/', { state: { showLogin: true } })}
              >
                Log In
              </button>
              <button
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                onClick={() => navigate('/', { state: { showRegister: true } })}
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      <Meteors count={40} angle={220} />

      <Navbar
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        openLogin={() => navigate('/', { state: { showLogin: true } })}
        openRegister={() => navigate('/', { state: { showRegister: true } })}
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
      />

      {/* Profile Hero Section */}
<section className="relative py-12 px-4">
  <div className="max-w-5xl mx-auto">
    <Item variant="outline" className="p-8 md:p-10 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-sm">
      <ItemContent className="text-center space-y-4">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
          <User className="h-8 w-8 text-primary" />
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          {role === 'Administrator' ? (
            <>Admin Dashboard 🛠️</>
          ) : (
            <>Jambo, {user?.username}! 👋</>
          )}
        </h1>

        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-md mx-auto">
          {role === 'Administrator'
            ? 'Manage SErraND with precision and control'
            : 'Your hub for services, chats, orders, and more on the SErraND platform'}
        </p>

        {role === 'Administrator' && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 rounded-full text-sm font-medium mt-2">
            <ShieldCheck className="h-4 w-4" />
            Administrator Access
          </div>
        )}
      </ItemContent>
    </Item>
  </div>
</section>

      {/* Main Content */}
      <section className="py-12 px-4 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card - Left Sidebar */}
<div className="lg:col-span-1">
  <Card className="border-gray-200 dark:border-gray-800 shadow-sm h-fit">
    <CardHeader className="text-center pb-6">
      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
        <User className="h-10 w-10 text-primary" />
      </div>
      <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
        Your Profile
      </CardTitle>
      <CardDescription className="text-base">
        {role === 'Administrator' ? 'Administrator' : 'Service Provider'}
      </CardDescription>
    </CardHeader>

    <CardContent className="space-y-8">

      {showEditForm ? (
        /* Edit Mode */
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Edit Profile</h3>

          {editFormErrors.all && (
            <div className="bg-destructive/10 border border-destructive rounded-lg p-3">
              <p className="text-destructive text-sm">{editFormErrors.all.join(' ')}</p>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium mb-2 block">Username</label>
              <Input
                value={editFormData.username}
                onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Email</label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Input
                value={editFormData.location}
                onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
              />
            </div>

            {user?.is_service_provider && (
              <>
                <div>
                  <label className="text-sm font-medium mb-2 block">Business Name</label>
                  <Input
                    value={editFormData.business_name}
                    onChange={(e) => setEditFormData({ ...editFormData, business_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Service Provided</label>
                  <Input
                    value={editFormData.service_provided}
                    onChange={(e) => setEditFormData({ ...editFormData, service_provided: e.target.value })}
                    placeholder="e.g., Make Up Artist"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={4}
                    placeholder="Business Description"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Business Hours</label>
                  <Input
                    value={editFormData.business_hours}
                    onChange={(e) => setEditFormData({ ...editFormData, business_hours: e.target.value })}
                    placeholder="e.g., Mon-Fri 9AM-5PM"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleEditProfile} disabled={editProfileMutation.isLoading} className="flex-1">
              {editProfileMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditForm(false);
                setEditFormErrors({});
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (

        <div className="flex-1">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Username</p>
              <p className="font-medium">{user?.username}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Location</p>
              <p className="font-medium">{user?.location || 'Not set'}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Role</p>
              <p className="font-medium">{role}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Joined</p>
              <p className="font-medium">
                {user?.date_joined ? new Date(user.date_joined).toLocaleDateString() : 'Not available'}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Reputation Score</p>
              <p className="font-medium">{user?.reputation_score?.toFixed(1) || '50.0'}</p>
            </div>
          </div>

          {user?.provider && (
            <>
              {/* Provider Info */}
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">Business</p>
                  <p className="font-medium">{user.provider.business_name}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">Service</p>
                  <p className="font-medium">{user.provider.service_provided || 'Not set'}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">Trust Score</p>
                  <p className="font-medium">
                    {user.provider.trust_score?.toFixed(1) || '0.0'} (Tier {user.provider.trust_tier || 'E'})
                  </p>
                </div>
              </div>

              {/* Debt Management */}
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Total Debt</p>
                <p className={`font-medium ${isDebtPending ? 'text-yellow-600' : isDebtHigh ? 'text-red-600' : ''}`}>
                  {isDebtPending ? 'Pending...' : `${totalDebt.toFixed(2)} KES`}
                </p>
                {isDebtHigh && !isDebtPending && (
                  <p className="text-red-500 text-sm mt-1">(Exceeds {debtThreshold} KES threshold)</p>
                )}
              </div>

              {totalDebt > 0 && (
                <Card className="border-blue-200 dark:border-blue-800">
                  <CardHeader>
                    <CardTitle className="text-lg">💰 Offset Debt</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg text-sm">
                      <strong>Payment Instructions:</strong><br />
                      Send to M-Pesa: <strong>0740 879 229</strong><br />
                      Then enter your M-Pesa number below.
                    </div>

                    <Input
                      type="number"
                      value={debtAmount}
                      onChange={(e) => setDebtAmount(e.target.value)}
                      placeholder="Amount to pay (KES)"
                      disabled={isDebtPending}
                    />
                    <Input
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Your M-Pesa Number (2547XXXXXXXX)"
                      disabled={isDebtPending}
                    />

                    <Button 
                      onClick={handleOffsetDebt} 
                      disabled={offsetDebtMutation.isLoading || isDebtPending}
                      className="w-full"
                    >
                      {offsetDebtMutation.isLoading ? 'Submitting...' : '💸 Submit Payment'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Provider Guidelines */}
              <Card>
                <CardHeader>
                  <CardTitle>Service Provider Guidelines</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-4">
                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="font-medium mb-2">Contact Information</p>
                    <p>To maintain security, please do not include your phone number on your profile. All communication should occur through SErraND's platform.</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="font-medium mb-2">Commission Structure</p>
                    <p>We charge a modest 10% commission only on transactions from new clients.</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="font-medium mb-2">Boost Your Visibility</p>
                    <p>Upload sample images of your work to get featured on the homepage for 72 hours.</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {user.is_superuser && (
            <Card className="border-red-200 dark:border-red-800">
              <CardContent className="pt-6">
                <p className="text-red-600 dark:text-red-400">
                  <strong>Admin Access:</strong>{' '}
                  <a href={`${process.env.REACT_APP_API_URL}/admin/`} className="underline hover:text-red-700">
                    Django Admin Panel
                  </a>
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Bottom Action Buttons */}
      <div className="pt-6 border-t">
        <div className="flex flex-col sm:flex-row gap-3 pt-6">
          <Button onClick={() => setShowEditForm(true)} className="flex-1">
            ✏️ Edit Profile
          </Button>
          <Button onClick={() => setShowScoringPanel(true)} variant="outline" className="flex-1">
            📊 How Scoring Works
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
</div>

{/* Chat Section */}
<div className="lg:col-span-2">
  <Card className="border-gray-200 dark:border-gray-800 shadow-sm h-full flex flex-col overflow-hidden" style={{ height: "calc(100vh - 180px)" }}>
    
    <CardHeader className="border-b flex-shrink-0">
      <div className="flex items-center justify-between">
        <CardTitle>Your Chats 💬</CardTitle>
        {selectedPartner && (
          <p className="text-sm text-muted-foreground">
            with {chatPartners?.find(p => p.id.toString() === selectedPartner)?.username || 'Unknown'}
          </p>
        )}
      </div>
    </CardHeader>

    <CardContent className="flex-1 flex flex-col lg:flex-row overflow-hidden p-0 min-h-0">

      {/* Chat Partners List - Scrollable */}
      <div className={`w-full lg:w-80 border-r border-gray-200 dark:border-gray-800 flex-shrink-0 overflow-hidden transition-all
        ${selectedPartner ? 'hidden lg:flex' : 'flex'}`}>
        
        <div className="flex flex-col h-full w-full">
          <div className="p-4 border-b flex-shrink-0">
            <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Chat Partners</h3>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 min-h-0" style={{ maxHeight: "calc(100vh - 280px)" }}>
            {chatPartners?.length === 0 || !chatPartners ? (
              <Empty className="py-12">
                <EmptyMedia><MessageCircle className="h-10 w-10 text-muted-foreground" /></EmptyMedia>
                <EmptyTitle>No chats yet</EmptyTitle>
                <EmptyDescription>Start a conversation from an active order</EmptyDescription>
              </Empty>
            ) : (
              <div className="space-y-1">
                {chatPartners.map((partner) => (
                  <div
                    key={partner.id}
                    onClick={() => handlePartnerClick(partner.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:bg-muted ${
                      selectedPartner === partner.id.toString() ? 'bg-primary/10 border border-primary/30' : ''
                    }`}
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                      👤
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{partner.username}</p>
                      <p className="text-xs text-muted-foreground">Tap to open chat</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-0">

        {selectedPartner ? (
          <>
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center gap-3 p-4 border-b bg-card flex-shrink-0">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => {
                  setSelectedPartner(null);
                  navigate('/profile');
                }}
              >
                ←
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white">👤</div>
                <p className="font-medium">
                  {chatPartners?.find(p => p.id.toString() === selectedPartner)?.username || 'Chat'}
                </p>
              </div>
            </div>

            {/* Messages Area - Strong scroll container */}
            <div 
              className="flex-1 p-4 overflow-y-auto space-y-4 bg-muted/30 min-h-0" 
              ref={messagesEndRef}
              style={{ scrollBehavior: 'smooth' }}
            >
              {isChatLoading || isMessagesFetching ? (
                <div className="h-full flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-muted-foreground">Loading messages...</p>
                  </div>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <p className="text-5xl mb-4 opacity-40">💬</p>
                    <p className="font-medium">No messages yet</p>
                    <p className="text-sm text-muted-foreground">Send the first message below</p>
                  </div>
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === user.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.sender === user.id
                          ? 'bg-primary text-primary-foreground rounded-br-none'
                          : 'bg-card border border-border rounded-bl-none'
                      }`}
                    >
                      <p>{msg.message}</p>
                      <p className="text-[10px] mt-1 opacity-70 text-right">
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            {canChatData?.can_chat ? (
              <div className="p-4 border-t bg-card flex-shrink-0">
                <div className="flex gap-2">
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={1}
                    className="resize-y min-h-[52px] max-h-[120px]"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <Button 
                    onClick={handleSendMessage}
                    disabled={sendMessageMutation.isLoading || !message.trim()}
                    size="icon"
                  >
                    ↑
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center border-t bg-card flex-shrink-0">
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-5">
                  <p className="text-destructive text-sm">
                    {canChatData?.error || 'Chat is only available during an active or scheduled order.'}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center p-8 hidden lg:flex">
            <div>
              <div className="text-6xl mb-6 opacity-40">💬</div>
              <h3 className="text-xl font-medium mb-2">Select a conversation</h3>
              <p className="text-muted-foreground">Choose a partner from the list on the left</p>
            </div>
          </div>
        )}

      </div>

    </CardContent>

  </Card>
</div>
        </div>

{user?.provider && (
  <div className="mt-10 lg:ml-[calc(33.333%+2rem)]">
    {/* Media Gallery — Images & Videos */}
    <Card className="border-gray-200 dark:border-gray-800">
      <CardHeader>
    	<CardTitle>Gallery (Images & Videos)</CardTitle>
	<CardDescription>
      	  Show off your work! You can upload up to <strong>15 images</strong> and <strong>2 short videos</strong> (max 60 sec).
    	</CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">

    {/* ====================== IMAGES SECTION ====================== */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">Images ({providerImages?.length || 0}/15)</h4>
          </div>

          {imageError && (
            <div className="bg-destructive/10 border border-destructive text-destructive text-sm rounded-lg p-3 mb-4">
              {imageError}
            </div>
          )}

          {/* Image Upload Area */}
          {!uploadImageMutation.isPending && providerImages?.length < 15 && (
            <form onSubmit={handleImageUpload} className="mb-6">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/30 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="text-center">
                  <p className="text-sm font-medium">Click to upload image</p>
                  <p className="text-xs text-muted-foreground">PNG, JPG, WEBP (max 10MB)</p>
            	</div>
                <input
              	  type="file"
              	  accept="image/*"
              	  onChange={(e) => {
                    setImageFile(e.target.files?.[0] || null);
                    setImageError('');
                  }}
                  className="hidden"
                />
              </label>

              {imageFile && (
                <div className="mt-3 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3 flex items-center gap-2">
                  📄 <span className="truncate">{imageFile.name}</span>
                </div>
              )}

              {imageFile && (
                <Button type="submit" className="w-full mt-3">
                  Upload Selected Image
                </Button>
              )}
            </form>
          )}

          {/* Upload Progress */}
          {uploadImageMutation.isPending && (
            <div className="mb-8 p-6 border rounded-xl bg-muted/50">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-muted rounded-lg animate-pulse flex items-center justify-center">📸</div>
                <div className="flex-1">
                  <p className="font-medium">Uploading image...</p>
                  <Progress value={imageProgress} className="mt-2" />
                </div>
              </div>
            </div>
          )}

          {/* Images Carousel */}
          {providerImages?.length > 0 ? (
            <Carousel opts={{ align: "start", loop: true }} className="w-full">
              <CarouselContent>
                {providerImages.map((img) => (
                  <CarouselItem key={img.id} className="basis-full sm:basis-1/2 lg:basis-1/3">
                    <div
                      className="relative group rounded-xl overflow-hidden aspect-video cursor-pointer bg-black"
                      onClick={() => setLightboxImage(img.image)}
                    >
                      <img
                        src={img.image}
                        alt="Work sample"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                     	  handleDeleteImage(img.id);   // This will trigger AlertDialog
                        }}
                      >
                        🗑️
                      </Button>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          ) : !uploadImageMutation.isPending && (
            <Empty>
              <EmptyMedia><Image className="h-10 w-10 text-muted-foreground" /></EmptyMedia>
              <EmptyTitle>No images yet</EmptyTitle>
              <EmptyDescription>Add images to showcase your work.</EmptyDescription>
            </Empty>
          )}
        </div>

        {/* ====================== VIDEOS SECTION ====================== */}
        <div className="border-t pt-8">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">Videos ({providerVideos?.length || 0}/2)</h4>
      	  </div>

      	  {videoError && (
            <div className="bg-destructive/10 border border-destructive text-destructive text-sm rounded-lg p-3 mb-4">
              {videoError}
            </div>
          )}

          {/* Video Upload Area */}
          {!uploadVideoMutation.isPending && providerVideos?.length < 2 && (
            <form onSubmit={(e) => { e.preventDefault(); uploadVideoMutation.mutate(); }} className="mb-6">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/30 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="text-center">
                  <p className="text-sm font-medium">Click to upload video</p>
                  <p className="text-xs text-muted-foreground">MP4, MOV (max 60 seconds)</p>
            	</div>
                <input
                  type="file"
              	  accept="video/*"
              	  onChange={(e) => {
                    setVideoFile(e.target.files?.[0] || null);
                    setVideoError('');
                  }}
                  className="hidden"
                />
              </label>

              {videoFile && (
                <div className="mt-3 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3 flex items-center gap-2">
                  🎥 <span className="truncate">{videoFile.name}</span>
            	</div>
              )}

              {videoFile && (
                <Button type="submit" className="w-full mt-3">
                  Upload Selected Video
                </Button>
              )}
            </form>
          )}

          {/* Video Upload Progress */}
          {uploadVideoMutation.isPending && (
            <div className="mb-8 p-6 border rounded-xl bg-muted/50">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-muted rounded-lg animate-pulse flex items-center justify-center">🎥</div>
                <div className="flex-1">
                  <p className="font-medium">Processing video...</p>
                  <Progress value={uploadProgress} className="mt-2" />
                </div>
              </div>
            </div>
          )}

          {/* Videos Grid */}
          {providerVideos?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {providerVideos.map((vid) => (
                <div key={vid.id} className="relative rounded-xl overflow-hidden bg-black group">
                  <VideoPlayer url={vid.video} className="w-full aspect-video" />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all"
                    onClick={() => setVideoToDelete(vid)}
                  >
                    🗑️
                  </Button>
                </div>
              ))}
            </div>
          ) : !uploadVideoMutation.isPending && (
            <Empty>
              <EmptyMedia><Video className="h-10 w-10 text-muted-foreground" /></EmptyMedia>
              <EmptyTitle>No videos yet</EmptyTitle>
              <EmptyDescription>Upload short videos to showcase your work.</EmptyDescription>
            </Empty>
          )}
        </div>

        {/* Lightbox */}
        {lightboxImage && (
          <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
            <img src={lightboxImage} alt="Full size" className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
            <button onClick={() => setLightboxImage(null)} className="absolute top-8 right-8 text-white text-6xl font-light hover:scale-110">×</button>
          </div>
        )}

        {/* Video Delete Confirmation (Original Custom Modal) */}
        {videoToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 border border-red-600">
              <h3 className="text-2xl font-bold text-white mb-4">Delete This Video?</h3>
              <div className="w-full bg-black rounded-lg overflow-hidden mb-6">
                <VideoPlayer url={videoToDelete.video} className="w-full max-h-96 mx-auto" />
              </div>
              <p className="text-gray-300 mb-8">This action cannot be undone.</p>
              <div className="flex gap-4">
                <Button
                  variant="destructive"
                  onClick={() => {
                    deleteVideoMutation.mutate(videoToDelete.id);
                    setVideoToDelete(null);
                  }}
                  disabled={deleteVideoMutation.isLoading}
                  className="flex-1"
                >
                  {deleteVideoMutation.isLoading ? 'Deleting...' : 'Yes, Delete'}
                </Button>
                <Button variant="outline" onClick={() => setVideoToDelete(null)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

      </CardContent>
    </Card>

  </div>
)}
{/* Admin Section */}
{user.is_superuser && (
  <div className="mt-10">
    <Card className="border-gray-200 dark:border-gray-800 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-2xl">
          ⚙️ Admin Management
        </CardTitle>
        <CardDescription>Assign services and manage categories</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* Assign Services */}
          <Card className="border-gray-200 dark:border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Assign Services</CardTitle>
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/50 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-400">
                  🔧
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleAssignService} className="space-y-6">
                {assignServiceErrors.all && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive">
                    {assignServiceErrors.all[0]}
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Select Provider</label>
                  <select
                    name="provider_id"
                    value={assignServiceForm.provider_id}
                    onChange={(e) => {
                      const providerId = e.target.value;
                      const selectedProvider = providers?.find(p => p.id.toString() === providerId);
                      setAssignServiceForm({
                        ...assignServiceForm,
                        provider_id: providerId,
                        service_name: selectedProvider?.service_provided || '',
                      });
                      setAssignServiceErrors({});
                    }}
                    className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:ring-2 focus:ring-ring"
                    disabled={providersLoading}
                  >
                    <option value="">Select Provider</option>
                    {providersLoading ? (
                      <option>Loading providers...</option>
                    ) : providers?.length > 0 ? (
                      providers.map(provider => (
                        <option key={provider.id} value={provider.id}>
                          {provider.business_name} ({provider.user.username})
                          {provider.service_provided ? ` — ${provider.service_provided}` : ''}
                        </option>
                      ))
                    ) : (
                      <option>No providers available</option>
                    )}
                  </select>
                  {assignServiceErrors?.provider_id && (
                    <p className="text-sm text-destructive">{assignServiceErrors.provider_id}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Service Name</label>
                  <Input
                    name="service_name"
                    value={assignServiceForm.service_name}
                    onChange={handleAssignServiceInputChange}
                    placeholder="e.g., Make Up Artist"
                  />
                  {assignServiceErrors?.service_name && (
                    <p className="text-sm text-destructive">{assignServiceErrors.service_name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Category</label>
                  <select
                    name="category"
                    value={assignServiceForm.category}
                    onChange={handleAssignServiceInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:ring-2 focus:ring-ring"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select Category</option>
                    {categoriesLoading ? (
                      <option>Loading categories...</option>
                    ) : categories?.length > 0 ? (
                      categories.map(category => (
                        <option key={category.id} value={category.slug}>
                          {category.name} {category.is_general ? '(General)' : ''}
                          {category.default_minimum_charge ? ` (${category.default_minimum_charge} KES min)` : ''}
                        </option>
                      ))
                    ) : (
                      <option>No categories available</option>
                    )}
                  </select>
                  {assignServiceErrors?.category && (
                    <p className="text-sm text-destructive">{assignServiceErrors.category}</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" disabled={assignServiceMutation.isLoading} className="flex-1">
                    {assignServiceMutation.isLoading ? 'Assigning...' : '✅ Assign Service'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      setAssignServiceForm({ provider_id: '', service_name: '', category: '' });
                      setAssignServiceErrors({});
                    }}
                    className="flex-1"
                  >
                    🗑️ Clear
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Categories & Services Management */}
          <Card className="border-gray-200 dark:border-gray-800 flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Manage Categories & Services</CardTitle>
                <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                  📂
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-1 flex flex-col">
              <div className="flex gap-3 mb-6">
                <Button 
                  onClick={() => {
                    setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
                    setEditingCategoryId(null);
                    setIsCategoryModalOpen(true);
                  }}
                  className="flex-1"
                >
                  ➕ Add Category
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
                    setEditingServiceId(null);
                    setIsServiceModalOpen(true);
                  }}
                  className="flex-1"
                >
                  ➕ Add Service
                </Button>
              </div>

              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search categories or services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Scrollable List */}
              <div className="flex-1 overflow-y-auto min-h-0 pr-2" style={{ maxHeight: "calc(100vh - 420px)" }}>
                {categoriesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : categories && categories.length > 0 ? (
                  <div className="space-y-4">
                    {categories
                      .filter(category => 
                        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        category.services?.some(s => 
                          s.name.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                      )
                      .map(category => (
                        <Card key={category.id} className="border-gray-200 dark:border-gray-800">
                          <CardContent className="p-5">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="font-semibold text-base">{category.name}</h3>
                                <p className="text-xs text-muted-foreground">Slug: {category.slug}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="ghost" onClick={() => handleEditCategory(category)}>
                                  ✏️
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteCategory(category.id)}>
                                  🗑️
                                </Button>
                              </div>
                            </div>

                            <div className="text-sm space-y-1 text-muted-foreground mb-4">
                              <p>Min Charge: {category.default_minimum_charge ? `${category.default_minimum_charge} KES` : 'Not set'}</p>
                              <p>General: {category.is_general ? 'Yes' : 'No'}</p>
                              {category.description && <p>{category.description}</p>}
                            </div>

                            <div>
                              <h4 className="text-sm font-medium mb-2">Services</h4>
                              {category.services && category.services.length > 0 ? (
                                <div className="space-y-2">
                                  {category.services.map(service => (
                                    <div key={service.id} className="flex justify-between items-center bg-muted/50 rounded-lg p-3 text-sm">
                                      <span>{service.name} {service.custom_minimum_charge && `(${service.custom_minimum_charge} KES)`}</span>
                                      <div className="flex gap-2">
                                        <Button size="sm" variant="ghost" onClick={() => handleEditService(service)}>
                                          ✏️
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => handleDeleteService(service.id)}>
                                          🗑️
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground italic">No services assigned</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                ) : (
                  <Empty className="py-12">
                    <EmptyTitle>No categories available</EmptyTitle>
                  </Empty>
                )}
              </div>
            </CardContent>
          </Card>

        </div>
      </CardContent>
    </Card>
  </div>
)}
      </section>

            {/* Scoring Panel Modal */}
      {showScoringPanel && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <BarChart3 className="h-6 w-6" />
                  How Scoring Works
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowScoringPanel(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-8 space-y-8 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="space-y-6">
                {/* Quality Ratings */}
                <div className="border border-blue-200 dark:border-blue-800 rounded-xl p-6 bg-blue-50/50 dark:bg-blue-950/30">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-blue-600 dark:text-blue-400 mb-3">
                    <Star className="h-5 w-5" /> Quality Ratings
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    After an order is completed, clients can rate providers (1-5 stars) with an optional review. 
                    Ratings are anonymous if chosen and directly impact the provider's trust score.
                  </p>
                </div>

                {/* Provider Trust Score */}
                <div className="border border-green-200 dark:border-green-800 rounded-xl p-6 bg-green-50/50 dark:bg-green-950/30">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-green-600 dark:text-green-400 mb-3">
                    <Trophy className="h-5 w-5" /> Provider Trust Score (0-100)
                  </h3>
                  <div className="space-y-3 text-sm text-muted-foreground ml-2">
                    <p>• <strong>Quality Ratings (40%)</strong>: Average of all client ratings</p>
                    <p>• <strong>Transaction Honesty (20%)</strong>: Lower cash payment usage increases score</p>
                    <p>• <strong>Service Smoothness (20%)</strong>: Based on acceptance, completion, and on-time rates</p>
                    <p>• <strong>M-Pesa Preference (20%)</strong>: Using M-Pesa payments boosts the score</p>
                  </div>
                  <div className="mt-6 p-5 bg-green-100 dark:bg-green-900/50 rounded-lg">
                    <p className="font-medium mb-2">Trust Tiers:</p>
                    <p className="text-sm">E (≤30) • D (≤45) • C (≤60) • B (≤80) • A (&gt;80)</p>
                    <p className="text-xs text-muted-foreground mt-3">Higher tiers improve visibility and client trust!</p>
                  </div>
                </div>

                {/* User Reputation Score */}
                <div className="border border-purple-200 dark:border-purple-800 rounded-xl p-6 bg-purple-50/50 dark:bg-purple-950/30">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-purple-600 dark:text-purple-400 mb-3">
                    <User className="h-5 w-5" /> User Reputation Score (0-100)
                  </h3>
                  <div className="space-y-3 text-sm text-muted-foreground ml-2">
                    <p>• <strong>Base score:</strong> 50 points</p>
                    <p>• <strong>Completed orders:</strong> +30% per successful completion</p>
                    <p>• <strong>Cancellations/Rejections:</strong> -20% per cancellation</p>
                  </div>
                  <div className="mt-6 p-5 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Higher scores show you're a reliable client and help providers trust you!</p>
                  </div>
                </div>
              </div>
            </CardContent>

            <div className="p-6 border-t">
              <Button onClick={() => setShowScoringPanel(false)} className="w-full">
                Got it
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Category Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  {editingCategoryId ? (
                    <><Pencil className="h-5 w-5" /> Edit Category</>
                  ) : (
                    <><Plus className="h-5 w-5" /> Add Category</>
                  )}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => {
                  setIsCategoryModalOpen(false);
                  setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
                  setEditingCategoryId(null);
                  setEditFormErrors({});
                }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-8 space-y-6 overflow-y-auto">
              <form onSubmit={handleCreateCategory} className="space-y-6">
                <div className="space-y-2">
                  <Label>Category Name</Label>
                  <Input
                    name="name"
                    value={categoryForm.name}
                    onChange={handleCategoryInputChange}
                    placeholder="Category Name"
                  />
                  {editFormErrors.name && <p className="text-sm text-destructive">{editFormErrors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input
                    name="slug"
                    value={categoryForm.slug}
                    onChange={handleCategoryInputChange}
                    placeholder="slug-name-here"
                  />
                  {editFormErrors.slug && <p className="text-sm text-destructive">{editFormErrors.slug}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Default Minimum Charge (KES)</Label>
                  <Input
                    name="default_minimum_charge"
                    type="number"
                    step="0.01"
                    value={categoryForm.default_minimum_charge}
                    onChange={handleCategoryInputChange}
                    placeholder="300.00"
                  />
                  {editFormErrors.default_minimum_charge && (
                    <p className="text-sm text-destructive">{editFormErrors.default_minimum_charge}</p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="is_general"
                    checked={categoryForm.is_general}
                    onCheckedChange={(checked) => 
                      setCategoryForm(prev => ({ ...prev, is_general: checked }))
                    }
                  />
                  <Label htmlFor="is_general" className="cursor-pointer">General Services Category</Label>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    name="description"
                    value={categoryForm.description}
                    onChange={handleCategoryInputChange}
                    placeholder="Category description..."
                    rows={4}
                  />
                </div>

                {editFormErrors.all && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-sm text-destructive">
                    {editFormErrors.all.join(' ')}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button type="submit" disabled={createCategoryMutation.isLoading} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    {createCategoryMutation.isLoading ? 'Saving...' : 'Save Category'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      setIsCategoryModalOpen(false);
                      setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
                      setEditingCategoryId(null);
                      setEditFormErrors({});
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Service Modal */}
      {isServiceModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  {editingServiceId ? (
                    <><Pencil className="h-5 w-5" /> Edit Service</>
                  ) : (
                    <><Plus className="h-5 w-5" /> Add Service</>
                  )}
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => {
                  setIsServiceModalOpen(false);
                  setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
                  setEditingServiceId(null);
                  setServiceFormErrors({});
                }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-8 space-y-6 overflow-y-auto">
              <form onSubmit={handleCreateService} className="space-y-6">
                <div className="space-y-2">
                  <Label>Service Name</Label>
                  <Input
                    name="name"
                    value={serviceForm.name}
                    onChange={handleServiceInputChange}
                    placeholder="Service Name"
                  />
                  {serviceFormErrors.name && <p className="text-sm text-destructive">{serviceFormErrors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    name="category"
                    value={serviceForm.category}
                    onChange={handleServiceInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:ring-2 focus:ring-ring"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select Category</option>
                    {categories?.map(category => (
                      <option key={category.id} value={category.slug}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {serviceFormErrors.category && (
                    <p className="text-sm text-destructive">{serviceFormErrors.category}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Custom Minimum Charge (KES)</Label>
                  <Input
                    name="custom_minimum_charge"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={serviceForm.custom_minimum_charge}
                    onChange={handleServiceInputChange}
                    placeholder="300.00"
                    disabled={categories?.find(cat => cat.slug === serviceForm.category)?.is_general === false}
                  />
                  {serviceFormErrors.custom_minimum_charge && (
                    <p className="text-sm text-destructive">{serviceFormErrors.custom_minimum_charge}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    name="description"
                    value={serviceForm.description}
                    onChange={handleServiceInputChange}
                    placeholder="Service Description"
                    rows={4}
                  />
                </div>

                {serviceFormErrors.all && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-sm text-destructive">
                    {serviceFormErrors.all.join(' ')}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button type="submit" disabled={createServiceMutation.isLoading} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    {createServiceMutation.isLoading ? 'Saving...' : 'Save Service'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      setIsServiceModalOpen(false);
                      setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
                      setEditingServiceId(null);
                      setServiceFormErrors({});
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
export default Profile;
